import { Getter, Setter } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { cloneDeep } from 'lodash-es';
import { useCallback } from 'react';
import { ICompanyMilestoneDataMode } from '../../../data-models/company-milestone.data-model';
import {
  createCompanyMilestone,
  deleteMilestone,
  editMilestone,
} from '../../../services/queries/MaggieMilestonesQueries';
import { milestonesByCompanyStateJ } from '../../../services/state/MilestonesStateJ';

// errors should be handled in the component

export const useMilestoneActions = () => {
  const handleCreateMilestone = useAtomCallback(
    useCallback(async (get: Getter, set: Setter, payload: Partial<ICompanyMilestoneDataMode>) => {
      if (!payload.companyId) {
        throw new Error('Company ID is required to create a milestone');
      }

      const res = await createCompanyMilestone(payload);
      // Read the resolved current state explicitly (functional updaters on
      // `atomWithDefault` widen `prev` to `T | Promise<T>` in TS).
      const prev = await get(milestonesByCompanyStateJ(payload.companyId));
      const next = prev ? [...cloneDeep(prev), res] : [res];
      set(milestonesByCompanyStateJ(payload.companyId), next);
      return res;
    }, [])
  );

  const handleEditMilestone = useAtomCallback(
    useCallback(async (get: Getter, set: Setter, payload: Partial<ICompanyMilestoneDataMode>) => {
      if (!payload.companyId || !payload.id) {
        throw new Error('companyId and id are required to edit a milestone');
      }

      const res = await editMilestone(payload.id, payload);
      const prev = await get(milestonesByCompanyStateJ(payload.companyId));
      if (!prev) {
        set(milestonesByCompanyStateJ(payload.companyId), null);
        return res;
      }
      const next = prev.map((ms) => (ms.id === payload.id ? res : ms));
      set(milestonesByCompanyStateJ(payload.companyId), next);
      return res;
    }, [])
  );

  const handleDeleteMilestone = useAtomCallback(
    useCallback(async (get: Getter, set: Setter, companyId: number, id: number) => {
      await deleteMilestone(id);
      const prev = await get(milestonesByCompanyStateJ(companyId));
      if (!prev) {
        set(milestonesByCompanyStateJ(companyId), null);
        return;
      }
      set(
        milestonesByCompanyStateJ(companyId),
        prev.filter((ms) => ms.id !== id)
      );
    }, [])
  );

  return {
    handleCreateMilestone,
    handleEditMilestone,
    handleDeleteMilestone,
  };
};
